import { useTranslation } from "react-i18next";
import { getPasswordStrength } from "@/utils/form";
import { PasswordRequirement } from "@/components/shared/form/FormInput";
import { JSX } from "react";

type Props = {
  password: string;
}

/**
 * PasswordRequirementsDisplay component for showing password strength and requirements
 *
 * @param {Props} props - The props object
 * @param {string} props.password - The password value to validate and display requirements for
 * @returns {JSX.Element | null} The rendered password requirements component or null if no password
 */
export const PasswordRequirementsDisplay = ({ password }: Props): JSX.Element | null => {
  const { t } = useTranslation();

  if (!password) return null;

  const passwordStrength = getPasswordStrength(password);

  return (
    <div data-cy="twinput-password-req" className="text-sm my-2 select-none">
      <p className="font-semibold mb-1">
        <span className="mr-2 dark:text-white">{t("components:input.password.title")}</span>
        <span data-cy="twinput-password-req-label" className={`text-${passwordStrength.id === null || passwordStrength.id === 0 ? "red" :
          passwordStrength.id === 1 ? "orange" :
            passwordStrength.id === 2 ? "yellow" : "green"}-500`}
        >
          {passwordStrength.strength ? t("components:input.password.strength" + passwordStrength.id) : t("components:input.password.enterpassword")}
        </span>
      </p>
      <PasswordRequirement requirementMet={passwordStrength.requiredLength}>
        {t("components:input.password.lengthcheck")}
      </PasswordRequirement>
      <PasswordRequirement requirementMet={passwordStrength.hasNumberOrSpecial}>
        {t("components:input.password.numberandspecialcheck")}
      </PasswordRequirement>
      <PasswordRequirement requirementMet={passwordStrength.hasUppercase}>
        {t("components:input.password.uppercasecheck")}
      </PasswordRequirement>
    </div>
  );
};

export default PasswordRequirementsDisplay;
